<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useSupportStore } from '@/stores/support.store'
import { useUsersStore } from '@/stores'
import { storeToRefs } from 'pinia'
import MessageBubble from '@core/components/MessageBubble.vue'

const supportStore = useSupportStore()
const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const inputText = ref('')
const messagesContainer = ref(null)
const imageInput = ref(null)
const uploading = ref(false)

const {
  messages,
  loadingMessages,
  sendingMessage,
} = storeToRefs(supportStore)

onMounted(async () => {
  await supportStore.loadMessages()
  scrollToBottom()
})

onUnmounted(() => {
  supportStore.leaveSupportView()
})

async function handleSend() {
  const text = inputText.value.trim()
  if (!text && !uploading.value) return

  inputText.value = ''
  await supportStore.sendMessage({ text })
  scrollToBottom()
}

function handleKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleSend()
  }
}

async function handleImageUpload(event) {
  const file = event.target.files?.[0]
  if (!file) return

  uploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', file)

    const axios = (await import('@axios')).default
    const { data } = await axios.post('/media/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    if (data.imageId) {
      await supportStore.sendMessage({ imageIds: [data.imageId] })
      scrollToBottom()
    }
  } catch (err) {
    console.error('Image upload failed', err)
  } finally {
    uploading.value = false
    if (imageInput.value) imageInput.value.value = ''
  }
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

function isOwnMessage(msg) {
  return msg.senderType === 'user' && msg.senderId === myUser.value?._id
}

function showSender(msg, idx) {
  if (idx === 0) return true
  const prev = messages.value[idx - 1]
  
  return prev.senderId !== msg.senderId || prev.senderType !== msg.senderType
}

watch(() => messages.value.length, () => {
  scrollToBottom()
})
</script>

<template>
  <div class="content-wrapper mt-6">
    <VCard class="support-page-card">
      <!-- Header -->
      <div class="support-page-header d-flex align-center px-6">
        <VIcon
          color="warning"
          size="24"
          class="mr-2"
        >
          mdi-headset
        </VIcon>
        <span class="text-h6">Support</span>
      </div>

      <!-- Messages -->
      <div
        ref="messagesContainer"
        class="support-page-messages"
      >
        <div
          v-if="loadingMessages"
          class="d-flex justify-center pa-10"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </div>

        <div
          v-else-if="messages.length === 0"
          class="pa-10 text-center text-medium-emphasis"
        >
          <VIcon
            size="64"
            color="grey-lighten-1"
            class="mb-3"
          >
            mdi-headset
          </VIcon>
          <div class="text-body-1">
            How can we help? Send us a message and we'll get back to you shortly.
          </div>
        </div>

        <template v-else>
          <MessageBubble
            v-for="(msg, idx) in messages"
            :key="msg._id"
            :message="msg"
            :is-own="isOwnMessage(msg)"
            :show-sender="showSender(msg, idx)"
          />
        </template>
      </div>

      <!-- Composer -->
      <div class="support-page-composer px-4 py-3">
        <div class="d-flex align-end gap-2">
          <VBtn
            icon
            variant="text"
            size="small"
            color="medium-emphasis"
            :loading="uploading"
            @click="imageInput?.click()"
          >
            <VIcon size="20">
              mdi-image-plus
            </VIcon>
          </VBtn>
          <input
            ref="imageInput"
            type="file"
            accept="image/*"
            style="display: none"
            @change="handleImageUpload"
          >
          <VTextarea
            v-model="inputText"
            placeholder="Type a message..."
            variant="outlined"
            density="compact"
            rows="1"
            max-rows="4"
            auto-grow
            hide-details
            class="flex-1"
            style="font-size: 16px"
            @keydown="handleKeydown"
          />
          <VBtn
            icon
            color="primary"
            size="small"
            :disabled="!inputText.trim() || sendingMessage"
            :loading="sendingMessage"
            @click="handleSend"
          >
            <VIcon size="20">
              mdi-send
            </VIcon>
          </VBtn>
        </div>
      </div>
    </VCard>
  </div>
</template>

<style scoped>
.support-page-card {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  max-height: 800px;
}

.support-page-header {
  height: 56px;
  min-height: 56px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.support-page-messages {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.support-page-composer {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
